<template>
  <div
    v-if="hasChildren || pageType !== 'node'"
    :class="[
      'mx-auto flex w-full max-w-281.25 flex-col border-t border-[#595959]',
      { 'last:border-b': depth === 0 },
    ]"
  >
    <NuxtLink
      v-if="!hasChildren && href"
      :to="href"
      :target="isExternal ? (node.target ?? '_blank') : undefined"
      :rel="isExternal ? 'noopener noreferrer' : undefined"
      class="group flex w-full items-center justify-between py-3 text-white transition-colors duration-300"
    >
      <span
        :class="[
          'text-xl/[105%] transition-colors duration-300 select-none xl:text-[26px]/[115%]',
          isCurrentPage
            ? 'text-white underline decoration-1 underline-offset-5'
            : 'group-hover:text-gray text-white',
        ]"
      >
        {{ node.label }}
      </span>
      <span
        v-if="node.type === 'external'"
        class="group-hover:text-gray relative flex h-5 w-5 shrink-0 items-center justify-center text-white transition-colors duration-300 md:h-6 md:w-6"
      >
        <ArrowIcon class="h-4.5 w-4.5 md:h-5 md:w-5" />
      </span>
    </NuxtLink>

    <button
      v-else-if="hasChildren"
      type="button"
      class="group flex w-full cursor-pointer items-center justify-between py-3 text-left text-white transition-colors duration-300"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    >
      <span
        :class="[
          'text-xl/[105%] transition-colors duration-300 select-none xl:text-[26px]/[115%]',
          isCurrentPage
            ? 'text-white underline decoration-1 underline-offset-5'
            : 'group-hover:text-gray text-white',
        ]"
      >
        {{ node.label }}
      </span>
      <span
        class="group-hover:text-gray relative flex h-5 w-5 shrink-0 items-center justify-center text-white transition-colors duration-300 md:h-6 md:w-6"
      >
        <Transition name="icon-switch">
          <MinusIcon v-if="isOpen" class="absolute h-5 w-5 md:h-6 md:w-6" />
          <PlusIcon v-else class="absolute h-5 w-5 md:h-6 md:w-6" />
        </Transition>
      </span>
    </button>

    <span v-else class="w-full py-3 text-white transition-colors duration-300">
      <span class="text-xl/[105%] select-none xl:text-[26px]/[115%]">
        {{ node.label }}
      </span>
    </span>

    <div
      v-if="hasChildren"
      :inert="!isOpen || undefined"
      :style="{ transitionDuration: dynamicDuration }"
      :class="[
        'grid transition-all ease-in-out',
        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
      ]"
    >
      <div
        class="flex flex-col overflow-hidden pl-5 md:pl-10 [&>*:first-child]:border-t-0"
      >
        <NodePageItem
          v-for="child in node.children"
          :key="child.id"
          :node="child"
          :depth="depth + 1"
          :page-type="child.type"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { MenuItem } from '~/types'
import ArrowIcon from '~/components/icons/ArrowIcon.vue'
import PlusIcon from '~/components/icons/PlusIcon.vue'
import MinusIcon from '~/components/icons/MinusIcon.vue'

interface Props {
  node: MenuItem
  depth?: number
  pageType: string
}

const props = withDefaults(defineProps<Props>(), {
  depth: 0,
})

const localePath = useLocalePath()
const route = useRoute()

const hasChildren = computed(
  () => !!props.node?.children && props.node.children.length > 0,
)

const isExternal = computed(() => props.node.type === 'external')

const href = computed(() => {
  const raw = props.node.url || props.node.link
  if (!raw) return null
  if (isExternal.value) return raw
  return localePath(raw)
})

const isCurrentPage = computed(() => {
  return href.value && route.path === href.value
})

const isAncestorOfCurrent = computed(() => {
  if (!href.value) return false
  return route.path.startsWith(href.value + '/')
})

const isOpen = ref(false)

onMounted(() => {
  if (hasChildren.value && isAncestorOfCurrent.value) {
    isOpen.value = true
  }
})

const dynamicDuration = computed(() => {
  if (!hasChildren.value) return '300ms'

  const childCount = props.node.children!.length
  const calculatedTime = 300 + childCount * 40
  const duration = Math.min(calculatedTime, 800)

  return `${duration}ms`
})
</script>

<style scoped>
.icon-switch-enter-active,
.icon-switch-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.icon-switch-enter-from {
  opacity: 0;
  transform: rotate(-90deg) scale(0.5);
}

.icon-switch-leave-to {
  opacity: 0;
  transform: rotate(90deg) scale(0.5);
}
</style>
